<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <soft-alert
                    v-if="alertTable.state"
                    :color="alertTable.color"
                    class="mb-3"
                    dismissible
                >
                    <span class="text-sm">{{ alertTable.message }}</span>
                </soft-alert>
            </div>

            <div class="col-12">
                <table-component
                    :headers="headers"
                    :items="lonjas.data"
                    :spinner="spinner"
                    name="Lonjas"
                >
                    <template #headers>
                        <filters-lonja
                            v-model:search="search"
                            v-model:onlyActive="onlyActive"
                            @search="applyFilters"
                            @clear="cleanFilter"
                            @refresh="getLonjas(lonjas.current_page || 1)"
                        >
                            <div class="d-flex gap-2 flex-wrap align-items-center">
                                <soft-button
                                    variant="gradient"
                                    color="primary"
                                    size="sm"
                                    @click="showModalForm()"
                                >
                                    <i class="fas fa-plus me-1"></i>
                                    Nueva grupo de inmobiliarias
                                </soft-button>

                                <span class="badge bg-light text-dark border ms-auto px-3 py-2">
                                    <i class="fas fa-building me-1 text-secondary"></i>
                                    Total: {{ lonjas.total || 0 }}
                                </span>
                            </div>
                        </filters-lonja>
                    </template>

                    <template #columnRender="{ header, value }">
                        <!-- Logo -->
                        <span v-if="header.field === 'logo'">
                            <div class="lonja-logo">
                                <img
                                    v-if="value.logo"
                                    :src="value.logo"
                                    :alt="value.name"
                                    @error="onLogoError"
                                />
                                <i v-else class="fas fa-building text-secondary"></i>
                            </div>
                        </span>

                        <!-- Nombre -->
                        <span v-else-if="header.field === 'name'" class="fw-semibold text-dark">
                            {{ value.name }}
                            <span v-if="value.description" class="d-block text-xxs text-muted text-truncate">
                                {{ value.description }}
                            </span>
                        </span>

                        <!-- Slug -->
                        <span v-else-if="header.field === 'slug'">
                            <code class="text-xs">{{ value.slug }}</code>
                        </span>

                        <!-- Count inmobiliarias -->
                        <span v-else-if="header.field === 'inmobiliarias_count'">
                            <span class="badge bg-info text-white">
                                <i class="fas fa-users me-1"></i>
                                {{ memberCount(value) }}
                            </span>
                        </span>

                        <!-- Estado -->
                        <span v-else-if="header.field === 'active'">
                            <soft-badge
                                :color="value.active ? 'success' : 'secondary'"
                                variant="gradient"
                                size="sm"
                            >
                                {{ value.active ? 'Activa' : 'Inactiva' }}
                            </soft-badge>
                        </span>

                        <!-- Acciones -->
                        <span v-else-if="header.field === 'actions'" class="d-flex gap-1">
                            <router-link
                                :to="{ name: 'LonjasDetail', params: { id: value.id } }"
                                class="table-action-link"
                                title="Ver detalle"
                            >
                                <i class="fas fa-eye"></i>
                            </router-link>
                            <a
                                href="#"
                                class="table-action-link"
                                @click.prevent="showModalForm(value)"
                                title="Editar"
                            >
                                <i class="fas fa-edit"></i>
                            </a>
                            <a
                                href="#"
                                class="table-action-link"
                                :class="value.active ? 'text-warning' : 'text-success'"
                                @click.prevent="onToggleActive(value)"
                                :title="value.active ? 'Desactivar' : 'Activar'"
                            >
                                <i :class="value.active ? 'fas fa-toggle-off' : 'fas fa-toggle-on'"></i>
                            </a>
                            <a
                                href="#"
                                class="table-action-link text-danger"
                                @click.prevent="showModalDelete(value)"
                                title="Eliminar"
                            >
                                <i class="fas fa-trash"></i>
                            </a>
                        </span>
                    </template>

                    <template #pagination>
                        <div class="d-flex justify-content-center mt-3">
                            <base-pagination
                                :current-page="lonjas.current_page || 1"
                                :pageCount="lonjas.last_page || 1"
                                @nextPage="pageChangeHandle('next')"
                                @previousPage="pageChangeHandle('previous')"
                                @loadPage="pageChangeHandle"
                            />
                        </div>
                    </template>
                </table-component>

                <!-- Modal crear/editar -->
                <soft-modal
                    @close="toggleModalForm"
                    :modalActive="modalActiveForm"
                    v-if="modalActiveForm"
                    size="lg"
                >
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-building me-2 text-primary"></i>
                            {{ selectedLonja && selectedLonja.id ? 'Editar Grupo de Inmobiliarias' : 'Nuevo Grupo de Inmobiliarias' }}
                        </h5>
                    </template>
                    <form-lonja :lonja="selectedLonja" @close="onFormClose" />
                </soft-modal>

                <!-- Modal confirmación eliminar -->
                <soft-modal
                    @close="toggleModalDelete"
                    :modalActive="modalActiveDelete"
                    v-if="modalActiveDelete"
                    size="sm"
                >
                    <div class="text-center p-3">
                        <div class="lonja-delete-icon mx-auto mb-3">
                            <i class="fas fa-exclamation-triangle text-danger" style="font-size: 1.75rem;"></i>
                        </div>
                        <h5 class="font-weight-bolder mb-2">Confirmar Eliminación</h5>
                        <p class="text-sm text-secondary mb-2">
                            ¿Seguro que deseas eliminar la lonja
                            <strong>{{ selectedLonja?.name }}</strong>?
                        </p>
                        <p
                            v-if="memberCount(selectedLonja) > 0"
                            class="text-xs text-warning mb-3"
                        >
                            <i class="fas fa-info-circle me-1"></i>
                            <strong>{{ memberCount(selectedLonja) }}</strong>
                            inmobiliaria(s) dejarán de compartir MLS.
                        </p>
                        <div class="d-flex gap-2 justify-content-center">
                            <soft-button
                                variant="outline"
                                color="secondary"
                                size="sm"
                                @click="toggleModalDelete"
                            >
                                Cancelar
                            </soft-button>
                            <soft-button
                                variant="gradient"
                                color="danger"
                                size="sm"
                                @click="confirmDelete"
                                :loading="isLoading"
                            >
                                <i class="fas fa-trash me-1"></i>
                                Eliminar
                            </soft-button>
                        </div>
                    </div>
                </soft-modal>

                <!-- Loading overlay -->
                <div v-if="isLoading" class="loading-overlay">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import tableComponent from '@/components/tableComponent.vue';
import SoftBadge from '@/components/SoftBadge.vue';
import SoftModal from '@/components/SoftModal.vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftAlert from '@/components/SoftAlert.vue';
import BasePagination from '@/components/pagination/BasePagination.vue';
import FiltersLonja from './components/filtersLonja.vue';
import FormLonja from './components/formLonja.vue';
import { useLonja } from './useLonja.js';

export default {
    name: 'lonjas',
    components: {
        tableComponent,
        SoftBadge,
        SoftModal,
        SoftButton,
        SoftAlert,
        BasePagination,
        FiltersLonja,
        FormLonja
    },
    setup() {
        const {
            headers,
            lonjas,
            spinner,
            isLoading,
            alertTable,
            search,
            onlyActive,
            modalActiveForm,
            modalActiveDelete,
            selectedLonja,
            getLonjas,
            applyFilters,
            cleanFilter,
            pageChangeHandle,
            showModalForm,
            toggleModalForm,
            showModalDelete,
            toggleModalDelete,
            confirmDelete,
            toggleActive,
            closeModalAndReload
        } = useLonja();

        getLonjas();

        const memberCount = (lonja) => {
            if (!lonja) return 0;
            if (typeof lonja.inmobiliarias_count === 'number') return lonja.inmobiliarias_count;
            if (Array.isArray(lonja.inmobiliarias)) return lonja.inmobiliarias.length;
            return 0;
        };

        const onToggleActive = async (lonjaItem) => {
            await toggleActive(lonjaItem);
        };

        const onFormClose = (message) => {
            closeModalAndReload(message);
        };

        const onLogoError = (e) => {
            e.target.style.display = 'none';
        };

        return {
            headers,
            lonjas,
            spinner,
            isLoading,
            alertTable,
            search,
            onlyActive,
            modalActiveForm,
            modalActiveDelete,
            selectedLonja,
            getLonjas,
            applyFilters,
            cleanFilter,
            pageChangeHandle,
            showModalForm,
            toggleModalForm,
            showModalDelete,
            toggleModalDelete,
            confirmDelete,
            onToggleActive,
            onFormClose,
            memberCount,
            onLogoError
        };
    }
};
</script>

<style lang="scss" scoped>
.lonja-logo {
    width: 40px;
    height: 40px;
    border-radius: 0.5rem;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.text-truncate {
    max-width: 220px;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.375rem;
    color: #8392AB;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }

    &.text-danger:hover {
        background-color: #fef2f2;
        color: #ea0606;
    }

    &.text-warning:hover {
        background-color: #fff7e6;
        color: #e6a600;
    }

    &.text-success:hover {
        background-color: #e8f7ed;
        color: #2dce89;
    }
}

.modal-title {
    color: #344767;
    font-weight: 600;
}

.lonja-delete-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(1px);
}
</style>
